import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Typography, Divider, Row, Col, Alert } from 'antd';
import { formatCurrency } from '@/shared/utils/formatters';
import LoanScheduleComponent from './LoanScheduleComponent';
import {AppapproDTO} from "@/shared/api/model";

interface LoanOfficerRecommendationViewProps {
  data?: any;
  formikProps?: any;
  readOnly?: boolean;
}

/**
 * Component to display loan officer's recommended loan details
 * Used in the MEMBER_DEDUCTION_FORM node for the maker to review recommendations
 */
const LoanOfficerRecommendationView: React.FC<LoanOfficerRecommendationViewProps> = ({ 
  data, 
  formikProps,
  readOnly = false
}) => {
  const { Title, Text } = Typography;
  
  // Extract loan data from the context
  const values = formikProps?.values || data || {};
  const valueData = values.valueData || {};
  const [modifiedAppapproDTO, setModifiedAppapproDTO] = useState<AppapproDTO | null>(null);
  
  // Get the original and recommended values
  const originalLoanAmount = valueData.loanAmount;
  const originalLoanPeriod = valueData.lnPeriod;
  const recommendedLoanAmount = valueData.recommendedLoanAmt;
  const recommendedLoanPeriod = valueData.recommendedLnPeriod;
  
  // Check if recommendations exist
  const hasRecommendations = recommendedLoanAmount || recommendedLoanPeriod;

  // Set up the AppapproDTO for the loan schedule component
  useEffect(() => {
    if (valueData) {
      // Create a modified DTO using recommended values if they exist
      const dto: AppapproDTO = {
        ...valueData,
        // Use recommended values if they exist, otherwise use original values
        loanAmount: recommendedLoanAmount || originalLoanAmount,
        lnPeriod: recommendedLoanPeriod || originalLoanPeriod
      };
      setModifiedAppapproDTO(dto);
    }
  }, [valueData, recommendedLoanAmount, recommendedLoanPeriod, originalLoanAmount, originalLoanPeriod]);
  
  // Format currency and number values
  const formatAmount = (amount: number) => {
    return formatCurrency(amount);
  };
  
  return (
    <div className="space-y-4">
      <Card title="Loan Officer Recommendations" style={{ marginBottom: 16 }}>
        {hasRecommendations ? (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Original Loan Amount">
                {formatAmount(originalLoanAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Recommended Loan Amount">
                <Text strong style={{ color: '#1890ff' }}>
                  {formatAmount(recommendedLoanAmount)}
                </Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="Original Loan Period (Months)">
                {originalLoanPeriod}
              </Descriptions.Item>
              <Descriptions.Item label="Recommended Loan Period (Months)">
                <Text strong style={{ color: '#1890ff' }}>
                  {recommendedLoanPeriod}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            
            <Divider />
            
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                The loan officer has recommended changes to the original loan application.
                Please review these recommendations before proceeding with the deduction form.
              </Text>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Text type="secondary">
              No recommendations have been provided by the loan officer.
              The original loan details will be used.
            </Text>
            
            <Divider />
            
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Loan Amount">
                {formatAmount(originalLoanAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Loan Period (Months)">
                {originalLoanPeriod}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Card>

      {/* Loan Schedule Component */}
      <Card title="Loan Repayment Schedule" style={{ marginBottom: 16 }}>
        <Alert
          message={hasRecommendations ? "Recommended Schedule Preview" : "Original Schedule Preview"}
          description={hasRecommendations 
            ? `This schedule is based on the recommended loan amount of ${formatAmount(recommendedLoanAmount)} and period of ${recommendedLoanPeriod} months.`
            : `This schedule is based on the original loan amount of ${formatAmount(originalLoanAmount)} and period of ${originalLoanPeriod} months.`
          }
          type={hasRecommendations ? "success" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
        />


        {/* @ts-ignore */}
        <LoanScheduleComponent appapproDTO={modifiedAppapproDTO} />
      </Card>
    </div>
  );
};

export default LoanOfficerRecommendationView;
